import React, { useState, useEffect } from 'react';
import { graphql, StaticQuery } from 'gatsby';
import PropTypes from 'prop-types';

import BlogPost from '../../Blog/BlogCard';

import {
  Wrapper, StyledContainer, NoResults, ViewMore,
} from './styles';

// * The amount of new posts to be added to the page when a user presses show more
const postIncrement = 4;

/*
 * articles: {array} The array of article objects that is passed in by the wrapping static query
 */

function CultureAllBlogPosts({ sectionId, articles, lang }) {
  if (!articles) return null;

  const [currentPosts, setCurrentPosts] = useState([]);
  const [maxCount, setMaxCount] = useState(1);
  const [disabled, setDisabled] = useState(false);

  // * Stores the articles that should be visible in state

  useEffect(() => {
    const newPosts = articles.filter(({ category }) => {
      const CulturePosts = category.find((el) => el.id === 'prodigy-culture');
      return !!CulturePosts;
    });

    setCurrentPosts(newPosts);
    setMaxCount(1);
  }, []);

  // * Checks to see when we are showing all available posts, and then disables the ability to View more
  useEffect(() => {
    setDisabled(postIncrement * maxCount >= currentPosts.length);
  }, [currentPosts, maxCount]);

  return (
    <Wrapper>
      <StyledContainer id={sectionId}>
        {currentPosts.length === 0 && <NoResults>No Results Found</NoResults>}
        {currentPosts.map((post, index) => {
          if (index >= postIncrement * maxCount) return null;
          return (
            <BlogPost
              key={post.id}
              data={post}
              blogUrl="careers-blog"
              lang={lang}
            />
          );
        })}
        <ViewMore onClick={() => setMaxCount(maxCount + 1)} disabled={disabled}>
          View more posts
        </ViewMore>
      </StyledContainer>
    </Wrapper>
  );
}
CultureAllBlogPosts.propTypes = {
  articles: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  sectionId: PropTypes.string,
  lang: PropTypes.string,
};
CultureAllBlogPosts.defaultProps = {
  sectionId: null,
  lang: 'en-ca',
};
// * StaticQuery Component that retrieves and passes in all the articles
const withStaticQueryBlogPosts = ({ lang }) => (
  <StaticQuery
    query={graphql`
        {
          prodigy {
            pagedCultureArticles {
              id
              lang
              url
              author {
                name {
                  text
                }
                description {
                  text
                }
              }
              publishDate
              category {
                text
                id
                title
              }
              title
              titleShort
              metaData {
                metaDescription
                metaNoIndex
              }
              headingData {
                image {
                  url
                }
                imageExpanded {
                  imageBlogArticle {
                    url
                    altTag
                  }
                  imageBlogArticleSharp {
                    publicURL
                    childImageSharp {
                      gatsbyImageData(
                        quality: 90
                        placeholder: BLURRED
                        layout: FULL_WIDTH
                        formats: [AUTO, WEBP]
                      )
                    }
                  }
                  imageThumbnail {
                    url
                    altTag
                  }
                  imageThumbnailSharp {
                    publicURL
                    childImageSharp {
                      gatsbyImageData(
                        quality: 90
                        placeholder: BLURRED
                        layout: FULL_WIDTH
                        formats: [AUTO, WEBP]
                      )
                    }
                  }
                  imageFullsize {
                    url
                    altTag
                  }
                  imageFullsizeSharp {
                    publicURL
                    childImageSharp {
                      gatsbyImageData(
                        quality: 90
                        placeholder: BLURRED
                        layout: FULL_WIDTH
                        formats: [AUTO, WEBP]
                      )
                    }
                  }
                  imageCard {
                    url
                    altTag
                  }
                  imageCardSharp {
                    publicURL
                    childImageSharp {
                      gatsbyImageData(
                        quality: 90
                        placeholder: BLURRED
                        layout: FULL_WIDTH
                        formats: [AUTO, WEBP]
                      )
                    }
                  }
                  imageCultureHero {
                    url
                    altTag
                  }
                  imageCultureHeroSharp {
                    publicURL
                    childImageSharp {
                      gatsbyImageData(
                        quality: 90
                        placeholder: BLURRED
                        layout: FULL_WIDTH
                        formats: [AUTO, WEBP]
                      )
                    }
                  }
                }
                teaserTextHtml
                teaserTextShortHtml
              }
            }
          }
        }
      `}
    render={({ prodigy }) => {
      const localizedArticles = prodigy.pagedCultureArticles
        ? prodigy.pagedCultureArticles.filter(
          // this filter matches the in-en locale with all blog posts as currently none exist, however for the main-en it only matches main-en blogs
          (article) => article.lang === (lang === 'en-in' ? article.lang : 'en-ca'),
        )
        : [];
      return <CultureAllBlogPosts articles={localizedArticles} lang={lang} />;
    }}
  />
);

export default withStaticQueryBlogPosts;
